import { Suspense, lazy } from 'react';
import { createBrowserRouter, Outlet } from 'react-router';
import { dataRoutes } from './menus';
import NotFound from '@/pages/404';
import { PageLoading } from '@/components/PageLoading';
import { ContentLayout } from '@/layouts/BaseLayout';
import { AuthGuard } from '@/components/AuthGuard';
import { AdminGuard } from '@/components/AdminGuard';
import { RootRedirect } from '@/components/RootRedirect';
import { DataRouteConfig } from '@/types';

const SignInPage = lazy(() => import('@/pages/Auth/SignIn'));
const SignUpPage = lazy(() => import('@/pages/Auth/SignUp'));
const Landing = lazy(() => import('@/pages/Landing'));
const ProjectList = lazy(() => import('@/pages/ProjectList'));
const CadEditor = lazy(() => import('@/pages/CadEditor'));
const ProjectDashboard = lazy(() => import('@/pages/ProjectDashboard'));
// const TableList = lazy(() => import('@/pages/TableList'));
// const Dashboard = lazy(() => import('@/pages/Dashboard'));

// Create router using data mode with createBrowserRouter
export const router = createBrowserRouter([
  // Public routes
  {
    id: 'login',
    path: '/login',
    Component: () => (
      <Suspense fallback={<PageLoading />}>
        <SignInPage />
      </Suspense>
    ),
  },
  {
    id: 'register',
    path: '/register',
    Component: () => (
      <Suspense fallback={<PageLoading />}>
        <SignUpPage />
      </Suspense>
    ),
  },
  {
    id: 'landing',
    path: '/landing',
    Component: () => (
      <Suspense fallback={<PageLoading />}>
        <Landing />
      </Suspense>
    ),
  },

  // Auth-protected: project list
  {
    id: 'projects',
    path: '/projects',
    Component: () => (
      <AuthGuard>
        <ContentLayout>
          <Suspense fallback={<PageLoading />}>
            <ProjectList />
          </Suspense>
        </ContentLayout>
      </AuthGuard>
    ),
  },
  // {
  //   id: 'table-list',
  //   path: '/table-list',
  //   Component: () => (
  //     <AuthGuard>
  //       <ContentLayout>
  //         <Suspense fallback={<PageLoading />}>
  //           <TableList />
  //         </Suspense>
  //       </ContentLayout>
  //     </AuthGuard>
  //   ),
  // },
  // {
  //   id: 'dashboard',
  //   path: '/dashboard',
  //   Component: () => (
  //     <AuthGuard>
  //       <ContentLayout>
  //         <Suspense fallback={<PageLoading />}>
  //           <Dashboard />
  //         </Suspense>
  //       </ContentLayout>
  //     </AuthGuard>
  //   ),
  // },

  // Auth-protected: CAD editor (settings drawer in editor)
  {
    id: 'project-editor',
    path: '/projects/:id/editor',
    Component: () => (
      <AuthGuard>
        <Suspense fallback={<PageLoading />}>
          <CadEditor />
        </Suspense>
      </AuthGuard>
    ),
  },

  // Auth-protected: Project dashboard
  {
    id: 'project-dashboard',
    path: '/projects/:id/dashboard',
    Component: () => (
      <AuthGuard>
        <ContentLayout>
          <Suspense fallback={<PageLoading />}>
            <ProjectDashboard />
          </Suspense>
        </ContentLayout>
      </AuthGuard>
    ),
  },

  // Auth-protected: admin routes (admin role required)
  {
    id: 'admin',
    path: '/admin',
    Component: () => (
      <AuthGuard>
        <AdminGuard>
          <ContentLayout>
            <Suspense fallback={<PageLoading />}>
              <Outlet />
            </Suspense>
          </ContentLayout>
        </AdminGuard>
      </AuthGuard>
    ),
    children: [
      ...dataRoutes.map((route: DataRouteConfig) => ({
        id: route.id,
        path: route.path === '/' ? undefined : route.path?.replace(/^\//, ''),
        index: route.path === '/' ? true : undefined,
        Component: route.Component,
        loader: route.loader,
        action: route.action,
        errorElement: route.errorElement,
      })),
      {
        path: '*',
        Component: NotFound,
      },
    ],
  },

  // Root redirect: /projects if authenticated, /login if not
  {
    id: 'root',
    path: '/',
    Component: RootRedirect,
  },

  // 404 catch-all
  {
    path: '*',
    Component: NotFound,
  },
]);
